import { useMemo } from "react"
import { useHomeSessionsV2 } from "@/common/queries/use-home-sessions-v2"
import { get } from "radash"

import type { BannerData, HomeService } from "@be-miniapp/api"
import { v1GetTierName } from "@be-miniapp/api"
import { CACHE_KEYS } from "@be-miniapp/platform"
import { useAggressiveCache } from "@be-miniapp/ui"

export interface HomeBackground {
  tier: number
  home_background: string
  profile_background: string
  home_content: string
  home_content_ios: string
}

/**
 * Optimized hook for home data with React Query caching
 * Returns immediately available cached data and updates in background
 */
export function useOptimizedHomeData() {
  // Home sessions with aggressive caching
  const { data: homeSessions, isLoading: isLoadingHome } = useHomeSessionsV2()

  // User loyalty with aggressive caching
  const { data: userLoyalty, isLoading: isLoadingLoyalty } = useAggressiveCache(
    {
      cacheKey: CACHE_KEYS.USER_LOYALTY,
      queryKey: ["user-loyalty"],
      queryFn: async () => (await v1GetTierName({})).data,
      ttl: 7 * 24 * 60 * 60 * 1000, // 7 days cache for loyalty tier info
      staleTime: 60 * 60 * 1000, // 1 hour stale time
      refetchOnWindowFocus: true,
    }
  )

  // Memoized computed data
  const computedData = useMemo(() => {
    if (!homeSessions) {
      return {
        banners: [],
        services: [],
        servicesWithoutFilter: [],
        homeBackground: {} as HomeBackground,
        discoveryData: null,
      }
    }

    // Extract banners
    const bannersSession = homeSessions.sessions?.find(
      (session) => session.name === "Banner"
    )
    const banners = (bannersSession?.data as BannerData[]) || []

    // Extract services
    const servicesSession = homeSessions.sessions?.find(
      (session) => session.name === "Service"
    )
    const allServices = (servicesSession?.data as HomeService[]) || []

    const services = allServices.filter(
      (service) => !(service.max_web_version === 1)
    )

    // Extract discovery data
    const discoveryData = homeSessions.sessions?.find(
      (session) => session.name === "HomeDiscovery"
    )

    // Calculate home background
    const backgroundList = get(homeSessions, "tier_background.tiers", [])
    const tier = get(userLoyalty, "data.tier", -1)

    let homeBackground: HomeBackground

    if (tier === -1) {
      homeBackground = get(
        homeSessions,
        "tier_background.default",
        {}
      ) as HomeBackground
    } else {
      const tierBackground = backgroundList.find(
        (background: any) => background.tier === tier
      )
      homeBackground =
        tierBackground ||
        (get(homeSessions, "tier_background.default", {}) as HomeBackground)
    }

    return {
      banners,
      services,
      servicesWithoutFilter: services, // Same as services since filter is identical
      homeBackground,
      discoveryData,
    }
  }, [homeSessions, userLoyalty])

  return {
    ...computedData,
    userLoyalty,
    isLoading: isLoadingHome || isLoadingLoyalty,
  }
}
